'use client';
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';

interface WorkingHoursDay {
  enabled?: boolean;
  start?: string;
  end?: string;
}

interface WorkingHours {
  [day: string]: WorkingHoursDay;
}

export default function InteractiefPage() {
  const mountRef = useRef<HTMLDivElement>(null);
  const gameRef = useRef<any>(null);
  const [loaded, setLoaded] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [workingHoursReady, setWorkingHoursReady] = useState(false);
  const keysRef = useRef<Set<string>>(new Set());
  const contactLinesRef = useRef<string[]>([
    'Rijschool Simson - Almere',
    '',
    '• Telefoon: 06-20564200',
    '• Email: info@rijschoolsimson.nl',
    '• Website: rijschoolsimson.nl',
    '',
    '## Openingstijden',
    '• Ma: 13:30 - 18:00',
  '• Di: 10:30 - 19:30',
  '• Woe: 10:30 - 19:30',
  '• Do: 10:30 - 19:30',
  '• Vr: 10:30 - 19:30',
  '• Za: 10:30 - 19:30',
  ]);

  // Joystick state
  const joystickRef = useRef<HTMLDivElement>(null);
  const joystickActiveRef = useRef(false);
  const joystickOriginRef = useRef({ x: 0, y: 0 });
  const joystickDeltaRef = useRef({ x: 0, y: 0 });
  const knobRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsMobile('ontouchstart' in window || navigator.maxTouchPoints > 0);
  }, []);

  // Fetch Stephen Simson's working hours from Supabase
  useEffect(() => {
    async function fetchWorkingHours() {
      try {
        const supabase = createClient();
        const { data, error } = await supabase
          .from('instructor_profiles')
          .select('working_hours, first_name, last_name')
          .ilike('first_name', 'stephen')
          .ilike('last_name', 'simson')
          .single();

        if (error || !data) {
          setWorkingHoursReady(true);
          return;
        }

        const wh: WorkingHours = data.working_hours || {};
        const dayNames: { [key: string]: string } = {
          monday: 'Maandag',
          tuesday: 'Dinsdag',
          wednesday: 'Woensdag',
          thursday: 'Donderdag',
          friday: 'Vrijdag',
          saturday: 'Zaterdag',
          sunday: 'Zondag',
        };

        const lines: string[] = [
          'Rijschool Simson - Almere',
          '',
          '• Telefoon: 06-20564200',
          '• Email: info@rijschoolsimson.nl',
          '',
          '## Openingstijden',
        ];

        const orderedDays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
        let hasAnyDay = false;
        orderedDays.forEach(day => {
          const d = wh[day];
          if (d && d.enabled && d.start && d.end) {
            lines.push(`• ${dayNames[day]}: ${d.start} - ${d.end}`);
            hasAnyDay = true;
          }
        });

        if (!hasAnyDay) {
          lines.push('• Ma: 13:30 - 18:00');
          lines.push('• Di: 10:30 - 19:30');
          lines.push('• Woe: 10:30 - 19:30');
          lines.push('• Do: 10:30 - 19:30');
          lines.push('• Vr: 10:30 - 19:30');
          lines.push('• Za: 10:30 - 19:30');
        }

        contactLinesRef.current = lines;
      } catch {
        // keep defaults
      } finally {
        setWorkingHoursReady(true);
      }
    }
    fetchWorkingHours();
  }, []);

  // Joystick handlers
  const handleJoystickStart = useCallback((clientX: number, clientY: number) => {
    joystickActiveRef.current = true;
    joystickOriginRef.current = { x: clientX, y: clientY };
    joystickDeltaRef.current = { x: 0, y: 0 };
  }, []);

  const handleJoystickMove = useCallback((clientX: number, clientY: number) => {
    if (!joystickActiveRef.current) return;
    const maxRadius = 40;
    let dx = clientX - joystickOriginRef.current.x;
    let dy = clientY - joystickOriginRef.current.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist > maxRadius) {
      dx = (dx / dist) * maxRadius;
      dy = (dy / dist) * maxRadius;
    }
    joystickDeltaRef.current = { x: dx, y: dy };
    if (knobRef.current) {
      knobRef.current.style.transform = `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px))`;
    }
    // Map to keys — keep binary keys for keyboard compat, but store magnitude separately
    const threshold = 12;
    keysRef.current.delete('ArrowUp');
    keysRef.current.delete('ArrowDown');
    keysRef.current.delete('ArrowLeft');
    keysRef.current.delete('ArrowRight');
    if (dy < -threshold) keysRef.current.add('ArrowUp');
    if (dy > threshold) keysRef.current.add('ArrowDown');
    if (dx < -threshold) keysRef.current.add('ArrowLeft');
    if (dx > threshold) keysRef.current.add('ArrowRight');
  }, []);

  const handleJoystickEnd = useCallback(() => {
    joystickActiveRef.current = false;
    joystickDeltaRef.current = { x: 0, y: 0 };
    if (knobRef.current) {
      knobRef.current.style.transform = 'translate(-50%, -50%)';
    }
    keysRef.current.delete('ArrowUp');
    keysRef.current.delete('ArrowDown');
    keysRef.current.delete('ArrowLeft');
    keysRef.current.delete('ArrowRight');
  }, []);

  useEffect(() => {
    if (!workingHoursReady) return;
    let animationId: number;
    let THREE: any;

    async function init() {
      THREE = await import('three');
      if (!mountRef.current) return;

      const width = mountRef.current.clientWidth;
      const height = mountRef.current.clientHeight;

      // Scene
      const scene = new THREE.Scene();
      scene.background = new THREE.Color(0x87CEEB);
      scene.fog = new THREE.Fog(0x87CEEB, 80, 180);

      // Camera — more top-down for readable ground text
      const camera = new THREE.PerspectiveCamera(55, width / height, 0.1, 300);
      camera.position.set(0, 22, 10);
      camera.lookAt(0, 0, 0);

      // Renderer — high quality
      const renderer = new THREE.WebGLRenderer({ antialias: true, powerPreference: 'high-performance' });
      renderer.setSize(width, height);
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      renderer.shadowMap.enabled = true;
      renderer.shadowMap.type = THREE.PCFSoftShadowMap;
      renderer.outputColorSpace = THREE.SRGBColorSpace;
      renderer.toneMapping = THREE.ACESFilmicToneMapping;
      renderer.toneMappingExposure = 1.1;
      mountRef.current.appendChild(renderer.domElement);

      // Lights — improved quality
      const ambient = new THREE.AmbientLight(0xffeedd, 0.7);
      scene.add(ambient);

      const sun = new THREE.DirectionalLight(0xfff8e0, 1.6);
      sun.position.set(40, 70, 30);
      sun.castShadow = true;
      sun.shadow.mapSize.width = 4096;
      sun.shadow.mapSize.height = 4096;
      sun.shadow.camera.near = 0.5;
      sun.shadow.camera.far = 250;
      sun.shadow.camera.left = -100;
      sun.shadow.camera.right = 100;
      sun.shadow.camera.top = 100;
      sun.shadow.camera.bottom = -100;
      sun.shadow.bias = -0.0003;
      scene.add(sun);

      // Fill light
      const fillLight = new THREE.DirectionalLight(0xaaccff, 0.4);
      fillLight.position.set(-30, 20, -20);
      scene.add(fillLight);

      // Ground — higher quality texture via canvas
      const groundCanvas = document.createElement('canvas');
      groundCanvas.width = 512;
      groundCanvas.height = 512;
      const gCtx = groundCanvas.getContext('2d')!;
      gCtx.fillStyle = '#5aad5a';
      gCtx.fillRect(0, 0, 512, 512);
      // subtle grass pattern
      for (let i = 0; i < 2000; i++) {
        const gx = Math.random() * 512;
        const gy = Math.random() * 512;
        gCtx.fillStyle = Math.random() > 0.5 ? '#4e9e4e' : '#66bb66';
        gCtx.fillRect(gx, gy, 3, 3);
      }
      const groundTex = new THREE.CanvasTexture(groundCanvas);
      groundTex.wrapS = THREE.RepeatWrapping;
      groundTex.wrapT = THREE.RepeatWrapping;
      groundTex.repeat.set(20, 20);
      const groundGeo = new THREE.PlaneGeometry(200, 200);
      const groundMat = new THREE.MeshLambertMaterial({ map: groundTex });
      const ground = new THREE.Mesh(groundGeo, groundMat);
      ground.rotation.x = -Math.PI / 2;
      ground.receiveShadow = true;
      scene.add(ground);

      // Helper: box
      function makeBox(w: number, h: number, d: number, color: number) {
        const geo = new THREE.BoxGeometry(w, h, d);
        const mat = new THREE.MeshLambertMaterial({ color });
        const mesh = new THREE.Mesh(geo, mat);
        mesh.castShadow = true;
        mesh.receiveShadow = true;
        return mesh;
      }

      // Road helper
      function makeRoad(length: number, width: number, rotY: number, x: number, z: number) {
        // Road surface
        const roadCanvas = document.createElement('canvas');
        roadCanvas.width = 256;
        roadCanvas.height = 256;
        const rCtx = roadCanvas.getContext('2d')!;
        rCtx.fillStyle = '#4a4a5a';
        rCtx.fillRect(0, 0, 256, 256);
        // asphalt texture
        for (let i = 0; i < 500; i++) {
          const rx = Math.random() * 256;
          const ry = Math.random() * 256;
          rCtx.fillStyle = `rgba(${Math.random() > 0.5 ? 80 : 60},${Math.random() > 0.5 ? 80 : 60},90,0.4)`;
          rCtx.fillRect(rx, ry, 2, 2);
        }
        const roadTex = new THREE.CanvasTexture(roadCanvas);
        roadTex.wrapS = THREE.RepeatWrapping;
        roadTex.wrapT = THREE.RepeatWrapping;
        roadTex.repeat.set(1, length / width);

        const roadGeo = new THREE.PlaneGeometry(width, length);
        const roadMat = new THREE.MeshLambertMaterial({ map: roadTex });
        const road = new THREE.Mesh(roadGeo, roadMat);
        road.rotation.x = -Math.PI / 2;
        road.rotation.z = rotY;
        road.position.set(x, 0.01, z);
        road.receiveShadow = true;
        scene.add(road);

        // Dashed center line
        for (let i = -length / 2 + 2; i < length / 2; i += 4) {
          const dashGeo = new THREE.PlaneGeometry(0.3, 2.2);
          const dashMat = new THREE.MeshBasicMaterial({ color: 0xffffaa });
          const dash = new THREE.Mesh(dashGeo, dashMat);
          dash.rotation.x = -Math.PI / 2;
          dash.rotation.z = rotY;
          const angle = rotY;
          dash.position.set(
            x + Math.sin(angle) * i,
            0.025,
            z + Math.cos(angle) * i
          );
          scene.add(dash);
        }

        // Road edge lines
        [-width / 2 + 0.3, width / 2 - 0.3].forEach(offset => {
          for (let i = -length / 2 + 1; i < length / 2; i += 2) {
            const edgeGeo = new THREE.PlaneGeometry(0.15, 1.8);
            const edgeMat = new THREE.MeshBasicMaterial({ color: 0xffffff });
            const edge = new THREE.Mesh(edgeGeo, edgeMat);
            edge.rotation.x = -Math.PI / 2;
            edge.rotation.z = rotY;
            const angle = rotY;
            edge.position.set(
              x + Math.cos(angle) * offset + Math.sin(angle) * i,
              0.022,
              z - Math.sin(angle) * offset + Math.cos(angle) * i
            );
            scene.add(edge);
          }
        });
      }

      // Roads
      // ─── ROUNDABOUT ──────────────────────────────────────────────────────────
      // Road segments approaching the roundabout from 4 directions
      // The roundabout center is at (0, 0); inner radius ~7, outer radius ~12
      const roundaboutInner = 7;
      const roundaboutOuter = 12;
      const roadWidth = 9;
      const segmentLength = 60; // length of each arm

      // North arm (positive Z direction)
      makeRoad(segmentLength, roadWidth, 0, 0, roundaboutOuter + segmentLength / 2);
      // South arm (negative Z direction)
      makeRoad(segmentLength, roadWidth, 0, 0, -(roundaboutOuter + segmentLength / 2));
      // East arm (positive X direction)
      makeRoad(segmentLength, roadWidth, Math.PI / 2, roundaboutOuter + segmentLength / 2, 0);
      // West arm (negative X direction)
      makeRoad(segmentLength, roadWidth, Math.PI / 2, -(roundaboutOuter + segmentLength / 2), 0);

      // Roundabout ring road (annular road surface)
      {
        const ringCanvas = document.createElement('canvas');
        ringCanvas.width = 512;
        ringCanvas.height = 512;
        const rCtx = ringCanvas.getContext('2d')!;
        rCtx.fillStyle = '#4a4a5a';
        rCtx.fillRect(0, 0, 512, 512);
        for (let i = 0; i < 500; i++) {
          const rx = Math.random() * 512;
          const ry = Math.random() * 512;
          rCtx.fillStyle = `rgba(${Math.random() > 0.5 ? 80 : 60},${Math.random() > 0.5 ? 80 : 60},90,0.4)`;
          rCtx.fillRect(rx, ry, 2, 2);
        }
        const ringTex = new THREE.CanvasTexture(ringCanvas);

        // Use a ring geometry for the road surface
        const ringGeo = new THREE.RingGeometry(roundaboutInner, roundaboutOuter, 64);
        const ringMat = new THREE.MeshLambertMaterial({ map: ringTex, side: THREE.DoubleSide });
        const ringMesh = new THREE.Mesh(ringGeo, ringMat);
        ringMesh.rotation.x = -Math.PI / 2;
        ringMesh.position.set(0, 0.015, 0);
        ringMesh.receiveShadow = true;
        scene.add(ringMesh);

        // Dashed center line on the ring (mid-radius dashes)
        const midRadius = (roundaboutInner + roundaboutOuter) / 2;
        const dashCount = 24;
        for (let i = 0; i < dashCount; i++) {
          const angle = (i / dashCount) * Math.PI * 2;
          const dashGeo = new THREE.PlaneGeometry(0.3, 2.0);
          const dashMat = new THREE.MeshBasicMaterial({ color: 0xffffaa });
          const dash = new THREE.Mesh(dashGeo, dashMat);
          dash.rotation.x = -Math.PI / 2;
          dash.rotation.z = -angle;
          dash.position.set(Math.sin(angle) * midRadius, 0.03, Math.cos(angle) * midRadius);
          scene.add(dash);
        }

        // Outer edge white dashes
        const outerEdgeCount = 32;
        for (let i = 0; i < outerEdgeCount; i++) {
          const angle = (i / outerEdgeCount) * Math.PI * 2;
          const edgeGeo = new THREE.PlaneGeometry(0.15, 1.5);
          const edgeMat = new THREE.MeshBasicMaterial({ color: 0xffffff });
          const edge = new THREE.Mesh(edgeGeo, edgeMat);
          edge.rotation.x = -Math.PI / 2;
          edge.rotation.z = -angle;
          edge.position.set(Math.sin(angle) * (roundaboutOuter - 0.3), 0.025, Math.cos(angle) * (roundaboutOuter - 0.3));
          scene.add(edge);
        }
      }

      // Roundabout center island — grass circle
      {
        const islandGeo = new THREE.CircleGeometry(roundaboutInner - 0.3, 48);
        const islandMat = new THREE.MeshLambertMaterial({ color: 0x4aaa4a });
        const island = new THREE.Mesh(islandGeo, islandMat);
        island.rotation.x = -Math.PI / 2;
        island.position.set(0, 0.02, 0);
        island.receiveShadow = true;
        scene.add(island);

        // Low curb ring around island
        const curbGeo = new THREE.TorusGeometry(roundaboutInner - 0.15, 0.25, 8, 48);
        const curbMat = new THREE.MeshLambertMaterial({ color: 0xcccccc });
        const curb = new THREE.Mesh(curbGeo, curbMat);
        curb.rotation.x = -Math.PI / 2;
        curb.position.set(0, 0.25, 0);
        curb.castShadow = true;
        curb.receiveShadow = true;
        scene.add(curb);

        // Center decorative element — small green mound with a flower/post
        const moundGeo = new THREE.SphereGeometry(2.5, 16, 8, 0, Math.PI * 2, 0, Math.PI / 2);
        const moundMat = new THREE.MeshLambertMaterial({ color: 0x3a9a3a });
        const mound = new THREE.Mesh(moundGeo, moundMat);
        mound.position.set(0, 0, 0);
        mound.castShadow = true;
        mound.receiveShadow = true;
        scene.add(mound);

        // Decorative post/pole in center
        const poleGeo = new THREE.CylinderGeometry(0.15, 0.15, 4, 8);
        const poleMat = new THREE.MeshLambertMaterial({ color: 0xdddddd });
        const pole = new THREE.Mesh(poleGeo, poleMat);
        pole.position.set(0, 2, 0);
        pole.castShadow = true;
        scene.add(pole);

        // Decorative sphere on top of pole
        const sphereGeo = new THREE.SphereGeometry(0.6, 12, 10);
        const sphereMat = new THREE.MeshLambertMaterial({ color: 0xff6600 });
        const sphere = new THREE.Mesh(sphereGeo, sphereMat);
        sphere.position.set(0, 4.6, 0);
        sphere.castShadow = true;
        scene.add(sphere);

        // Small flowers / bushes around island
        const bushColors = [0xff4488, 0xffcc00, 0xff6600, 0xffffff];
        for (let i = 0; i < 8; i++) {
          const angle = (i / 8) * Math.PI * 2;
          const r = roundaboutInner * 0.55;
          const bushGeo = new THREE.SphereGeometry(0.55, 8, 6);
          const bushMat = new THREE.MeshLambertMaterial({ color: bushColors[i % bushColors.length] });
          const bush = new THREE.Mesh(bushGeo, bushMat);
          bush.position.set(Math.sin(angle) * r, 0.55, Math.cos(angle) * r);
          bush.castShadow = true;
          scene.add(bush);
        }
      }

      // Fill corner patches between road arms and ring (asphalt squares to hide grass gaps)
      {
        const patchMat = new THREE.MeshLambertMaterial({ color: 0x4a4a5a });
        const corners = [
          [roundaboutOuter * 0.65, roundaboutOuter * 0.65],
          [-roundaboutOuter * 0.65, roundaboutOuter * 0.65],
          [roundaboutOuter * 0.65, -roundaboutOuter * 0.65],
          [-roundaboutOuter * 0.65, -roundaboutOuter * 0.65],
        ];
        corners.forEach(([cx, cz]) => {
          const patchGeo = new THREE.PlaneGeometry(roadWidth * 0.9, roadWidth * 0.9);
          const patch = new THREE.Mesh(patchGeo, patchMat);
          patch.rotation.x = -Math.PI / 2;
          patch.position.set(cx, 0.012, cz);
          patch.receiveShadow = true;
          scene.add(patch);
        });
      }

      // ─── GROUND INFO PANELS ──────────────────────────────────────────────────
      // Large readable info panels on the ground showing actual page content
      function makeGroundInfoPanel(
        title: string,
        lines: string[],
        headerColor: string,
        bgColor: string,
        x: number,
        z: number,
        panelW: number = 22,
        panelH: number = 14
      ) {
        const CW = 1024;
        const CH = Math.round(CW * (panelH / panelW));
        const canvas = document.createElement('canvas');
        canvas.width = CW;
        canvas.height = CH;
        const ctx = canvas.getContext('2d')!;

        // Background
        ctx.fillStyle = bgColor;
        ctx.beginPath();
        ctx.roundRect(0, 0, CW, CH, 40);
        ctx.fill();

        // Header stripe
        ctx.fillStyle = headerColor;
        ctx.beginPath();
        ctx.roundRect(0, 0, CW, 130, [40, 40, 0, 0]);
        ctx.fill();

        // Border
        ctx.strokeStyle = headerColor;
        ctx.lineWidth = 12;
        ctx.beginPath();
        ctx.roundRect(6, 6, CW - 12, CH - 12, 36);
        ctx.stroke();

        // Title
        ctx.font = 'bold 80px Arial';
        ctx.fillStyle = '#ffffff';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.shadowColor = 'rgba(0,0,0,0.5)';
        ctx.shadowBlur = 8;
        ctx.fillText(title, CW / 2, 65);
        ctx.shadowBlur = 0;

        // Content lines
        ctx.textAlign = 'left';
        ctx.textBaseline = 'top';
        const lineH = Math.floor((CH - 150) / Math.max(lines.length, 1));
        const fontSize = Math.min(52, Math.max(32, lineH - 8));
        lines.forEach((line, i) => {
          const y = 148 + i * (fontSize + 10);
          if (y + fontSize > CH - 10) return;
          if (line === '') return;
          if (line.startsWith('##')) {
            ctx.font = `bold ${fontSize - 4}px Arial`;
            ctx.fillStyle = headerColor;
            ctx.fillText(line.replace('##', '').trim(), 40, y);
          } else if (line.startsWith('•')) {
            ctx.font = `${fontSize - 2}px Arial`;
            ctx.fillStyle = '#222222';
            ctx.fillText(line, 40, y);
          } else if (line.startsWith('€') || line.match(/^[\d€]/)) {
            ctx.font = `bold ${fontSize}px Arial`;
            ctx.fillStyle = headerColor;
            ctx.fillText(line, 40, y);
          } else {
            ctx.font = `bold ${fontSize - 2}px Arial`;
            ctx.fillStyle = '#111111';
            ctx.fillText(line, 40, y);
          }
        });

        const texture = new THREE.CanvasTexture(canvas);
        texture.anisotropy = renderer.capabilities.getMaxAnisotropy();
        const geo = new THREE.PlaneGeometry(panelW, panelH);
        const mat = new THREE.MeshBasicMaterial({ map: texture, transparent: true, depthWrite: false });
        const mesh = new THREE.Mesh(geo, mat);
        mesh.rotation.x = -Math.PI / 2;
        mesh.position.set(x, 0.06, z);
        scene.add(mesh);
      }

      // ─── TARIEVEN INFO PANEL ─────────────────────────────────────────────────
      makeGroundInfoPanel(
        '💶 TARIEVEN',
        [
          '## Losse rijlessen',
          '• Rijles 60 min:  € 70,-',
          '',
          '## Pakketten',
          '• 20 uur pakket:  € 1.665,-',
          '• 30 uur pakket:  € 2.335,-',
          '• 40 uur pakket:  € 3.040,-',
          '',
          '• Theorie cursus: € 59,-',
          '• Praktijk Examen: € 280,-',
        ],
        '#e8a020',
        '#fffbf0',
        20,
        -55,
        26,
        16
      );

      // ─── CONTACT INFO PANEL ──────────────────────────────────────────────────
      makeGroundInfoPanel(
        '📞 CONTACT',
        contactLinesRef.current,
        '#1a8fc1',
        '#f0f8ff',
        -20,
        55,
        26,
        16
      );

      // ─── HOMEPAGE INFO PANEL ─────────────────────────────────────────────────
      makeGroundInfoPanel(
        '🏠 RIJSCHOOL SIMSON',
        [
          'Rijles in Almere',
          '',
          '• Persoonlijke begeleiding',
          '• Flexibele lestijden',
          '• Betalen in termijnen',
          '• Snel rijbewijs halen!',
          '',
          '• Ervaren instructeurs',
          '• Hoog slagingspercentage',
        ],
        '#2a9d3a',
        '#f0fff4',
        -55,
        -20,
        26,
        16
      );

      // Small ground name labels on road
      function makeGroundLabel(text: string, x: number, z: number, color: string = '#ffffff') {
        const canvas = document.createElement('canvas');
        canvas.width = 512;
        canvas.height = 96;
        const ctx = canvas.getContext('2d')!;
        ctx.clearRect(0, 0, 512, 96);
        ctx.font = 'bold 56px Arial';
        ctx.fillStyle = color;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.strokeStyle = '#000000';
        ctx.lineWidth = 8;
        ctx.strokeText(text, 256, 48);
        ctx.fillText(text, 256, 48);
        const texture = new THREE.CanvasTexture(canvas);
        texture.anisotropy = renderer.capabilities.getMaxAnisotropy();
        const geo = new THREE.PlaneGeometry(10, 1.9);
        const mat = new THREE.MeshBasicMaterial({ map: texture, transparent: true, depthWrite: false });
        const mesh = new THREE.Mesh(geo, mat);
        mesh.rotation.x = -Math.PI / 2;
        mesh.position.set(x, 0.04, z);
        scene.add(mesh);
      }

      makeGroundLabel('TARIEVEN ↓', 20, -42, '#ffff44');
      makeGroundLabel('CONTACT ↓', -20, 42, '#44ffff');
      makeGroundLabel('HOMEPAGE ↓', -42, -20, '#ff88ff');

      // Block stacks (obstacles)
      const blockColors = [0xff6644, 0x44aaff, 0xffcc22, 0x88dd44, 0xcc44ff];
      const blockStacks: { mesh: THREE.Mesh; vx: number; vz: number; active: boolean }[] = [];

      function makeBlockStack(bx: number, bz: number) {
        const count = Math.floor(Math.random() * 3) + 2;
        const group = new THREE.Group();
        for (let i = 0; i < count; i++) {
          const color = blockColors[Math.floor(Math.random() * blockColors.length)];
          const block = makeBox(1.6, 1.6, 1.6, color);
          block.position.set(
            (Math.random() - 0.5) * 0.3,
            0.8 + i * 1.6,
            (Math.random() - 0.5) * 0.3
          );
          group.add(block);
        }
        group.position.set(bx, 0, bz);
        scene.add(group);
        const proxy = makeBox(2, count * 1.6, 2, 0x000000);
        proxy.material.visible = false;
        proxy.position.set(bx, (count * 1.6) / 2, bz);
        proxy.userData = { group, scattered: false, vx: 0, vz: 0 };
        scene.add(proxy);
        blockStacks.push({ mesh: proxy, vx: 0, vz: 0, active: false });
        return proxy;
      }

      const stackPositions = [
        [12, -5], [-12, -5], [12, 5], [-12, 5],
        [15, -30], [-15, -30], [8, -40], [-8, -40],
        [20, 10], [-20, 10]
      ];
      stackPositions.forEach(([bx, bz]) => makeBlockStack(bx, bz));

      // Ramp
      const rampGeo = new THREE.BoxGeometry(7, 0.3, 9);
      const rampMat = new THREE.MeshLambertMaterial({ color: 0xdd8833 });
      const ramp = new THREE.Mesh(rampGeo, rampMat);
      ramp.rotation.x = -Math.PI / 10;
      ramp.position.set(0, 0.9, -52);
      ramp.castShadow = true;
      ramp.receiveShadow = true;
      scene.add(ramp);
      makeGroundLabel('RAMP ↑', 0, -48, '#ffaa44');

      // Trees (cartoon, higher quality)
      function makeTree(tx: number, tz: number, scale: number = 1) {
        const trunk = makeBox(0.55 * scale, 2.2 * scale, 0.55 * scale, 0x8B4513);
        trunk.position.set(tx, 1.1 * scale, tz);
        scene.add(trunk);
        // Multi-sphere foliage for cartoon look
        const foliageColors = [0x228B22, 0x2da82d, 0x1e7a1e];
        [[0, 0, 0, 2.2], [-0.8, -0.5, 0, 1.4], [0.8, -0.5, 0, 1.4], [0, -0.5, 0.8, 1.3], [0, -0.5, -0.8, 1.3]].forEach(([fx, fy, fz, fr]) => {
          const foliage = new THREE.Mesh(
            new THREE.SphereGeometry(fr * scale, 10, 8),
            new THREE.MeshLambertMaterial({ color: foliageColors[Math.floor(Math.random() * foliageColors.length)] })
          );
          foliage.position.set(tx + fx * scale, (3.8 + fy) * scale, tz + fz * scale);
          foliage.castShadow = true;
          scene.add(foliage);
        });
      }

      const treePositions = [
        [20, -10, 1], [-20, -10, 1], [20, 10, 1.1], [-20, 10, 0.9],
        [25, -30, 1.2], [-25, -30, 1], [25, 0, 0.9], [-25, 0, 1.1],
        [15, -55, 1], [-15, -55, 1], [30, -20, 1.2], [-30, -20, 0.9],
        [35, 15, 1], [-35, 15, 1.1], [40, -5, 0.8], [-40, -5, 1],
      ];
      treePositions.forEach(([tx, tz, s]) => makeTree(tx, tz, s));

      // Clouds
      function makeCloud(cx: number, cy: number, cz: number, scale: number = 1) {
        const cloudMat = new THREE.MeshLambertMaterial({ color: 0xffffff });
        const parts = [
          [0, 0, 0, 2.5], [-2.5, -0.4, 0, 1.8], [2.5, -0.4, 0, 1.8],
          [0, -0.4, 2, 1.6], [0, -0.4, -2, 1.6], [-1.5, 0.5, 0, 1.4], [1.5, 0.5, 0, 1.4]
        ];
        parts.forEach(([px, py, pz, r]) => {
          const sphere = new THREE.Mesh(new THREE.SphereGeometry(r * scale, 10, 8), cloudMat);
          sphere.position.set(cx + px * scale, cy + py * scale, cz + pz * scale);
          scene.add(sphere);
        });
      }
      makeCloud(-20, 28, -30, 1.1);
      makeCloud(28, 32, -10, 1);
      makeCloud(10, 25, 22, 0.9);
      makeCloud(-32, 30, 5, 1.2);
      makeCloud(5, 35, -50, 1);

      // Car
      const carGroup = new THREE.Group();

      // Car body — brighter red with outline effect
      const bodyMat = new THREE.MeshLambertMaterial({ color: 0xff2222 });
      const body = new THREE.Mesh(new THREE.BoxGeometry(2.2, 0.85, 4.2), bodyMat);
      body.position.y = 0.65;
      body.castShadow = true;
      carGroup.add(body);

      // Bumpers
      const bumperMat = new THREE.MeshLambertMaterial({ color: 0xdddddd });
      const frontBumper = new THREE.Mesh(new THREE.BoxGeometry(2.0, 0.3, 0.2), bumperMat);
      frontBumper.position.set(0, 0.35, 2.2);
      carGroup.add(frontBumper);
      const backBumper = new THREE.Mesh(new THREE.BoxGeometry(2.0, 0.3, 0.2), bumperMat);
      backBumper.position.set(0, 0.35, -2.2);
      carGroup.add(backBumper);

      // Cabin
      const cabinMat = new THREE.MeshLambertMaterial({ color: 0xff5555 });
      const cabin = new THREE.Mesh(new THREE.BoxGeometry(1.7, 0.75, 2.4), cabinMat);
      cabin.position.set(0, 1.45, -0.2);
      cabin.castShadow = true;
      carGroup.add(cabin);

      // Windows
      const winMat = new THREE.MeshLambertMaterial({ color: 0x99ddff, transparent: true, opacity: 0.75 });
      const frontWin = new THREE.Mesh(new THREE.BoxGeometry(1.5, 0.55, 0.06), winMat);
      frontWin.position.set(0, 1.45, 1.0);
      carGroup.add(frontWin);
      const backWin = new THREE.Mesh(new THREE.BoxGeometry(1.5, 0.55, 0.06), winMat);
      backWin.position.set(0, 1.45, -1.4);
      carGroup.add(backWin);
      const leftWin = new THREE.Mesh(new THREE.BoxGeometry(0.06, 0.5, 1.8), winMat);
      leftWin.position.set(-0.86, 1.45, -0.2);
      carGroup.add(leftWin);
      const rightWin = new THREE.Mesh(new THREE.BoxGeometry(0.06, 0.5, 1.8), winMat);
      rightWin.position.set(0.86, 1.45, -0.2);
      carGroup.add(rightWin);

      // Wheels — higher quality
      const wheelMat = new THREE.MeshLambertMaterial({ color: 0x111111 });
      const rimMat = new THREE.MeshLambertMaterial({ color: 0xcccccc });
      const wheelGeo = new THREE.CylinderGeometry(0.45, 0.45, 0.35, 16);
      const rimGeo = new THREE.CylinderGeometry(0.22, 0.22, 0.36, 8);
      const wheelPositions = [
        [1.2, 0.45, 1.3], [-1.2, 0.45, 1.3],
        [1.2, 0.45, -1.3], [-1.2, 0.45, -1.3]
      ];
      const wheels: THREE.Mesh[] = [];
      wheelPositions.forEach(([wx, wy, wz]) => {
        const wheel = new THREE.Mesh(wheelGeo, wheelMat);
        wheel.rotation.z = Math.PI / 2;
        wheel.position.set(wx, wy, wz);
        wheel.castShadow = true;
        carGroup.add(wheel);
        wheels.push(wheel);
        const rim = new THREE.Mesh(rimGeo, rimMat);
        rim.rotation.z = Math.PI / 2;
        rim.position.set(wx, wy, wz);
        carGroup.add(rim);
      });

      // Headlights
      const lightMat = new THREE.MeshLambertMaterial({ color: 0xffffcc });
      [-0.65, 0.65].forEach(lx => {
        const light = new THREE.Mesh(new THREE.BoxGeometry(0.35, 0.22, 0.12), lightMat);
        light.position.set(lx, 0.72, 2.15);
        carGroup.add(light);
      });

      // Taillights
      const tailMat = new THREE.MeshLambertMaterial({ color: 0xff2200 });
      [-0.65, 0.65].forEach(lx => {
        const tail = new THREE.Mesh(new THREE.BoxGeometry(0.35, 0.22, 0.12), tailMat);
        tail.position.set(lx, 0.72, -2.15);
        carGroup.add(tail);
      });

      carGroup.position.set(0, 0, 0);
      scene.add(carGroup);

      // Car physics state
      const car = {
        x: 0, z: 0, vy: 0, y: 0,
        speed: 0, angle: 0,
        onGround: true,
        maxSpeed: 0.28,
        acceleration: 0.013,
        friction: 0.88,
        turnSpeed: 0.046
      };

      // Keyboard
      const keys = keysRef.current;

      function handleKeyDown(e: KeyboardEvent) {
        keys.add(e.key);
        if (['ArrowUp','ArrowDown','ArrowLeft','ArrowRight',' '].includes(e.key)) {
          e.preventDefault();
        }
      }
      function handleKeyUp(e: KeyboardEvent) {
        keys.delete(e.key);
      }
      window.addEventListener('keydown', handleKeyDown);
      window.addEventListener('keyup', handleKeyUp);

      // Resize
      function handleResize() {
        if (!mountRef.current) return;
        const w = mountRef.current.clientWidth;
        const h = mountRef.current.clientHeight;
        camera.aspect = w / h;
        camera.updateProjectionMatrix();
        renderer.setSize(w, h);
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      }
      window.addEventListener('resize', handleResize);

      setLoaded(true);

      // Game loop
      function animate() {
        animationId = requestAnimationFrame(animate);

        const forward = keys.has('ArrowUp') || keys.has('w');
        const backward = keys.has('ArrowDown') || keys.has('s');
        const left = keys.has('ArrowLeft') || keys.has('a');
        const right = keys.has('ArrowRight') || keys.has('d');

        // Analog joystick magnitude (0 = center, 1 = full push)
        const jDelta = joystickDeltaRef.current;
        const jMag = Math.min(1, Math.sqrt(jDelta.x * jDelta.x + jDelta.y * jDelta.y) / 40);
        // For keyboard use full speed; for joystick scale by magnitude
        const isJoystickActive = joystickActiveRef.current;
        const speedScale = isJoystickActive ? jMag : 1.0;

        if (forward) car.speed = Math.min(car.speed + car.acceleration * speedScale, car.maxSpeed * speedScale);
        else if (backward) car.speed = Math.max(car.speed - car.acceleration * speedScale, -car.maxSpeed * 0.6 * speedScale);
        else car.speed *= car.friction;

        // Clamp speed to current max based on joystick push
        if (isJoystickActive && forward) {
          car.speed = Math.min(car.speed, car.maxSpeed * speedScale);
        }
        if (isJoystickActive && backward) {
          car.speed = Math.max(car.speed, -car.maxSpeed * 0.6 * speedScale);
        }

        if (Math.abs(car.speed) > 0.001) {
          if (left) car.angle += car.turnSpeed * Math.sign(car.speed);
          if (right) car.angle -= car.turnSpeed * Math.sign(car.speed);
        }

        car.x += Math.sin(car.angle) * car.speed;
        car.z += Math.cos(car.angle) * car.speed;

        // Ramp detection
        const rampDist = Math.sqrt((car.x - 0) ** 2 + (car.z - (-52)) ** 2);
        if (rampDist < 4.5 && car.y < 2) {
          car.vy = 0.2;
          car.onGround = false;
        }

        // Gravity
        if (!car.onGround || car.y > 0) {
          car.vy -= 0.013;
          car.y += car.vy;
          if (car.y <= 0) {
            car.y = 0;
            car.vy = 0;
            car.onGround = true;
          }
        }

        // Block collision
        blockStacks.forEach(stack => {
          let dx = car.x - stack.mesh.position.x;
          const dz = car.z - stack.mesh.position.z;
          const dist = Math.sqrt(dx * dx + dz * dz);
          if (dist < 2.8 && !stack.active) {
            stack.active = true;
            stack.mesh.userData.scattered = true;
            const group: THREE.Group = stack.mesh.userData.group;
            group.children.forEach((child: any) => {
              child.userData.vx = (Math.random() - 0.5) * 0.35;
              child.userData.vz = (Math.random() - 0.5) * 0.35;
              child.userData.vy = Math.random() * 0.22 + 0.12;
            });
            car.speed *= -0.3;
          }
          if (stack.active) {
            const group: THREE.Group = stack.mesh.userData.group;
            group.children.forEach((child: any) => {
              if (child.userData.vy !== undefined) {
                child.position.x += child.userData.vx;
                child.position.z += child.userData.vz;
                child.position.y += child.userData.vy;
                child.userData.vy -= 0.016;
                child.rotation.x += 0.06;
                child.rotation.z += 0.06;
                if (child.position.y < 0.8) {
                  child.position.y = 0.8;
                  child.userData.vy *= -0.3;
                }
              }
            });
          }
        });

        // Boundary
        car.x = Math.max(-90, Math.min(90, car.x));
        car.z = Math.max(-90, Math.min(90, car.z));

        carGroup.position.set(car.x, car.y, car.z);
        carGroup.rotation.y = car.angle;

        const spinSpeed = car.speed * 3;
        wheels.forEach(w => { w.rotation.x += spinSpeed; });

        // Camera follow — top-down angle
        const camDist = 10;
        const camHeight = 26;
        const targetCamX = car.x - Math.sin(car.angle) * camDist;
        const targetCamZ = car.z - Math.cos(car.angle) * camDist;
        camera.position.x += (targetCamX - camera.position.x) * 0.07;
        camera.position.z += (targetCamZ - camera.position.z) * 0.07;
        camera.position.y += (camHeight + car.y - camera.position.y) * 0.07;
        camera.lookAt(car.x, car.y, car.z);

        renderer.render(scene, camera);
      }

      animate();

      gameRef.current = {
        cleanup: () => {
          cancelAnimationFrame(animationId);
          window.removeEventListener('keydown', handleKeyDown);
          window.removeEventListener('keyup', handleKeyUp);
          window.removeEventListener('resize', handleResize);
          renderer.dispose();
          if (mountRef.current && renderer.domElement.parentNode === mountRef.current) {
            mountRef.current.removeChild(renderer.domElement);
          }
        }
      };
    }

    init();

    return () => {
      if (gameRef.current) gameRef.current.cleanup();
    };
  }, [workingHoursReady]);

  return (
    <div className="flex flex-col" style={{ height: 'calc(100vh - 120px)', minHeight: 500 }}>
      {/* Header bar */}
      <div className="bg-gradient-to-r from-yellow-400 to-orange-400 px-4 py-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-2xl">🚗</span>
          <span className="font-bold text-white text-lg drop-shadow">Rijschool Simson - Interactief</span>
        </div>
        <div className="text-white text-sm hidden md:block opacity-90">
          Gebruik de pijltjestoetsen om te rijden • Rij over de info-panelen op de grond
        </div>
      </div>

      {/* Game canvas */}
      <div ref={mountRef} className="flex-1 relative bg-sky-300">
        {!loaded && (
          <div className="absolute inset-0 flex items-center justify-center bg-sky-400">
            <div className="text-white text-xl font-bold animate-pulse">🚗 Spel laden...</div>
          </div>
        )}
      </div>

      {/* Mobile joystick */}
      {isMobile && (
        <div className="bg-gray-900 bg-opacity-95 p-4 flex items-center justify-center" style={{ height: 140 }}>
          <div
            ref={joystickRef}
            className="relative select-none"
            style={{
              width: 110,
              height: 110,
              borderRadius: '50%',
              background: 'radial-gradient(circle at 40% 35%, rgba(255,255,255,0.18), rgba(0,0,0,0.55))',
              border: '3px solid rgba(255,255,255,0.3)',
              boxShadow: '0 4px 24px rgba(0,0,0,0.6), inset 0 2px 8px rgba(255,255,255,0.1)',
              touchAction: 'none',
            }}
            onTouchStart={e => {
              e.preventDefault();
              const t = e.touches[0];
              handleJoystickStart(t.clientX, t.clientY);
            }}
            onTouchMove={e => {
              e.preventDefault();
              const t = e.touches[0];
              handleJoystickMove(t.clientX, t.clientY);
            }}
            onTouchEnd={e => {
              e.preventDefault();
              handleJoystickEnd();
            }}
            onMouseDown={e => {
              handleJoystickStart(e.clientX, e.clientY);
            }}
          >
            {/* Outer ring decoration */}
            <div style={{
              position: 'absolute', inset: 6, borderRadius: '50%',
              border: '2px solid rgba(255,255,255,0.15)',
              pointerEvents: 'none'
            }} />
            {/* Directional arrows on base */}
            {[
              { label: '▲', top: 4, left: '50%', transform: 'translateX(-50%)' },
              { label: '▼', bottom: 4, left: '50%', transform: 'translateX(-50%)' },
              { label: '◀', left: 4, top: '50%', transform: 'translateY(-50%)' },
              { label: '▶', right: 4, top: '50%', transform: 'translateY(-50%)' },
            ].map((arrow, i) => (
              <span key={i} style={{
                position: 'absolute',
                fontSize: 11,
                color: 'rgba(255,255,255,0.35)',
                pointerEvents: 'none',
                lineHeight: 1,
                ...Object.fromEntries(Object.entries(arrow).filter(([k]) => !['label'].includes(k)))
              }}>{arrow.label}</span>
            ))}
            {/* Knob */}
            <div
              ref={knobRef}
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: 46,
                height: 46,
                borderRadius: '50%',
                background: 'radial-gradient(circle at 35% 30%, #ffe066, #f59e0b)',
                border: '3px solid rgba(255,255,255,0.6)',
                boxShadow: '0 3px 12px rgba(0,0,0,0.5), inset 0 1px 4px rgba(255,255,255,0.4)',
                transition: 'none',
                pointerEvents: 'none',
              }}
            />
          </div>
          <div className="ml-4 text-gray-400 text-xs text-center leading-relaxed">
            <div>🕹️ Sleep de stick</div>
            <div>om te rijden</div>
          </div>
        </div>
      )}
    </div>
  );
}
